import { Head, Link, useForm } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import AgentPasswordActions from '@/components/AgentPasswordActions'
import { PageProps } from '@/types'
import { ArrowLeft } from 'lucide-react'
import { ManagedUser, ROLE_DESCRIPTIONS, userPasswordEndpoints } from './shared'

interface Props extends PageProps {
  user: ManagedUser | null
  assignable_roles: string[]
  sendgrid_usable: boolean
}

function roleLabel(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1)
}

export default function AdminUserForm({ user, assignable_roles, sendgrid_usable }: Props) {
  const isEdit = !!user

  const { data, setData, post, patch, processing, errors } = useForm({
    user: {
      first_name: user?.first_name ?? '',
      last_name: user?.last_name ?? '',
      email: user?.email ?? '',
      role: user?.role ?? assignable_roles[0] ?? 'staff',
    },
    initial_password: '',
    send_welcome_email: sendgrid_usable,
  })

  function setUserField(field: 'first_name' | 'last_name' | 'email' | 'role', value: string) {
    setData('user', { ...data.user, [field]: value })
  }

  // The form data nests the user fields, but Rails reports validation errors
  // under the bare attribute name (`email`, not `user.email`), which Inertia's
  // generic cannot express for a nested payload.
  const fieldErrors = errors as unknown as Record<string, string | undefined>

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (isEdit && user) {
      patch(`/admin/users/${user.id}`)
    } else {
      post('/admin/users')
    }
  }

  return (
    <AppLayout>
      <Head title={isEdit ? `Edit ${user!.full_name}` : 'Add user'} />
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link href="/admin/users" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">
              {isEdit ? user!.full_name : 'Add user'}
            </h1>
            <p className="text-sm text-chestnut-charcoal/60 mt-1">
              {isEdit
                ? 'Change this account’s name, email or role.'
                : 'Creates an account with no agent record. It will not appear in the hierarchy tree.'}
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="card space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="first_name">First name</label>
              <input
                id="first_name"
                type="text"
                className="input"
                value={data.user.first_name}
                onChange={(e) => setUserField('first_name', e.target.value)}
              />
              {fieldErrors['first_name'] && <p className="mt-1 text-sm text-status-error">{fieldErrors['first_name']}</p>}
            </div>
            <div>
              <label className="label" htmlFor="last_name">Last name</label>
              <input
                id="last_name"
                type="text"
                className="input"
                value={data.user.last_name}
                onChange={(e) => setUserField('last_name', e.target.value)}
              />
              {fieldErrors['last_name'] && <p className="mt-1 text-sm text-status-error">{fieldErrors['last_name']}</p>}
            </div>
          </div>

          <div>
            <label className="label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className="input"
              value={data.user.email}
              onChange={(e) => setUserField('email', e.target.value)}
              required
            />
            {fieldErrors['email'] && <p className="mt-1 text-sm text-status-error">{fieldErrors['email']}</p>}
          </div>

          <div>
            <label className="label" htmlFor="role">Role</label>
            <select
              id="role"
              className="input"
              value={data.user.role}
              onChange={(e) => setUserField('role', e.target.value)}
            >
              {assignable_roles.map((role) => (
                <option key={role} value={role}>
                  {roleLabel(role)}
                </option>
              ))}
            </select>
            {ROLE_DESCRIPTIONS[data.user.role] && (
              <p className="mt-2 text-sm text-chestnut-charcoal/60">{ROLE_DESCRIPTIONS[data.user.role]}</p>
            )}
            {fieldErrors['role'] && <p className="mt-1 text-sm text-status-error">{fieldErrors['role']}</p>}
          </div>

          {!isEdit && (
            <>
              <div>
                <label className="label" htmlFor="initial_password">Temporary password (optional)</label>
                <input
                  id="initial_password"
                  type="text"
                  className="input"
                  placeholder="Leave blank to generate one"
                  value={data.initial_password}
                  onChange={(e) => setData('initial_password', e.target.value)}
                />
                <p className="mt-1 text-sm text-chestnut-charcoal/60">
                  If the welcome email cannot be sent, the password is shown once on the next screen so you can
                  pass it on.
                </p>
              </div>

              <label className="flex items-start gap-2 text-sm text-chestnut-charcoal">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={data.send_welcome_email}
                  disabled={!sendgrid_usable}
                  onChange={(e) => setData('send_welcome_email', e.target.checked)}
                />
                <span>
                  Send a welcome email with sign-in details
                  {!sendgrid_usable && (
                    <span className="block text-chestnut-charcoal/60">
                      Email delivery is not set up, so no welcome email can be sent.
                    </span>
                  )}
                </span>
              </label>
            </>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" className="btn-primary" disabled={processing}>
              {isEdit ? 'Save changes' : 'Create user'}
            </button>
            <Link href="/admin/users" className="text-sm text-chestnut-charcoal/70 hover:underline">
              Cancel
            </Link>
          </div>
        </form>

        {isEdit && user && !user.is_self && (
          <div className="card">
            <h2 className="text-lg font-semibold text-chestnut-charcoal">Password</h2>
            <p className="text-sm text-chestnut-charcoal/60 mt-1 mb-3">
              Set a password directly, email a reset link, or copy a one-time link to hand over in person.
            </p>
            <AgentPasswordActions
              agentId={user.id}
              agentName={user.full_name}
              sendgridConnected={sendgrid_usable}
              endpoints={userPasswordEndpoints(user.id)}
              passwordParamKey="user_password"
              variant="buttons"
            />
          </div>
        )}
      </div>
    </AppLayout>
  )
}
